import { useSearchParams } from "react-router";
import { AdminDataNotice } from "@/components/admin/data-notice";
import { FunnelChart } from "@/components/admin/funnel-chart";
import { PeriodSelect } from "@/components/admin/period-select";
import { useAdminView } from "@shared/client/admin-view";
import { usePageTitle } from "@shared/client/view";
import { AdminMeta, AdminPanel, StatCard, StatGroup } from "@shared/components/blocks";
import type { AdminMetrics } from "@shared/lib/admin-metrics";
import { formatCents, formatNumber, formatPercent } from "@shared/lib/format";

const PERIODS = [
  { days: 7, label: "7 jours" },
  { days: 30, label: "30 jours" },
  { days: 90, label: "90 jours" },
];

export default function AdminAnalyticsPage() {
  usePageTitle("Tracking — Administration Hune");
  const [params] = useSearchParams();
  const daysParam = params.get("days");
  const days = PERIODS.some((p) => p.days === Number(daysParam)) ? Number(daysParam) : 30;
  const { data, error: loadError } = useAdminView<{ metrics: AdminMetrics }>("/api/views/admin/analytics");
  if (!data && !loadError) return null;
  const metrics = data?.metrics ?? null;

  return (
    <>
      <AdminMeta note={<PeriodSelect days={days} options={PERIODS} />}>
        Funnel d’acquisition, churn et LTV • {days} derniers jours
      </AdminMeta>

      {loadError || !metrics ? (
        <AdminDataNotice error={loadError} />
      ) : (
        <>
          <StatGroup columns={3}>
            <StatCard
              label="Visiteurs landing"
              note={`${formatNumber(metrics.funnel[0]?.total ?? 0)} pages vues`}
              value={formatNumber(metrics.funnel[0]?.uniques ?? 0)}
            />
            <StatCard
              label="Taux vers le CTA"
              note="Visiteurs ayant cliqué un CTA"
              value={formatPercent(metrics.ctaRate)}
            />
            <StatCard
              label="Complétion onboarding"
              note="Terminé / démarré"
              tint="green"
              value={formatPercent(metrics.onboardingCompletionRate)}
            />
            <StatCard
              label="Churn mensuel"
              note={`${metrics.billing.churnedInPeriod} client${metrics.billing.churnedInPeriod > 1 ? "s" : ""} perdu${metrics.billing.churnedInPeriod > 1 ? "s" : ""} sur la période`}
              tint="rose"
              value={formatPercent(metrics.billing.monthlyChurnRate)}
            />
            <StatCard
              label="MRR"
              note={`${metrics.billing.activeClients} client${metrics.billing.activeClients > 1 ? "s" : ""} actif${metrics.billing.activeClients > 1 ? "s" : ""}`}
              tint="teal"
              value={formatCents(metrics.billing.mrrCents)}
            />
            <StatCard
              label="LTV estimée"
              note={`ARPA ${formatCents(metrics.billing.arpaCents)} / churn`}
              tint="amber"
              value={formatCents(metrics.billing.ltvCents)}
            />
          </StatGroup>

          <AdminPanel className="mt-4.5" title="Funnel d’acquisition">
            <FunnelChart steps={metrics.funnel} />
            <p className="mt-4 mb-0 text-[12px] leading-relaxed text-muted-foreground">
              Calculé à partir des événements nommés (<code className="font-mono">landing_viewed</code>,{" "}
              <code className="font-mono">cta_clicked</code>, <code className="font-mono">onboarding_completed</code>…)
              — contrat documenté dans <code className="font-mono">lib/analytics.ts</code>.
            </p>
          </AdminPanel>
        </>
      )}
    </>
  );
}
